import React from 'react'
import * as Permission from '../../constants/permission'
import { Col, Container, Row } from 'react-bootstrap'
import { AppContext } from '../../App'
import DashboardCard from '../../components/dashboard/dashboardCard'
import { Loading } from '../../components/ui/Loading/Loading'
import ReportModal, { ReportSettings } from './reportModal'
import { DateTime } from 'luxon'

const isDirector = [Permission.Admin, Permission.Director]
const isDirectorSuperManager = isDirector.concat([Permission.SupervisorManager])
const isDirectorSuperManagerEA = isDirectorSuperManager.concat([Permission.ExecAssistant])

const getReportSettings = (agedWipDate: string) => {
	const agedWipDateMonthEndInclusive = DateTime.fromISO(agedWipDate).plus({ month: 1 }).startOf('month').minus({ day: 1 }).toISODate() || ''

	const wipMovementSettings = (title: string, endpoint: string): ReportSettings => ({
		title,
		showStartDate: true,
		startDateIsRequired: true,
		startDateDefault: agedWipDate,
		showEndDate: true,
		endDateDefault: agedWipDateMonthEndInclusive,
		endDateLabel: 'To (Inclusive)',
		endDateIsRequired: true,
		showPartnerFilter: true,
		showManagerFilter: true,
		showWipFromDate: true,
		showCheckboxFilter: true,
		checkboxLabel: 'Show invoice number',
		reportEndpoint: ({ startDate, endDate, partnerStaffXpmID, managerStaffXpmID, wipFromDate, checkboxValue }) => {
			const clientWhereArr = []

			if (partnerStaffXpmID) {
				clientWhereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
			}
			if (managerStaffXpmID) {
				clientWhereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
			}

			const clientWhere = clientWhereArr.join('%2B')

			return `/${endpoint}?start=${startDate}${endDate ? `&end=${endDate}` : ''}${checkboxValue ? `&showinvoices=${checkboxValue}` : ''}${
				wipFromDate ? `&wipfromdate=${wipFromDate}` : ''
			}${clientWhere ? `&clientwhere=${clientWhere}` : ''}`
		},
	})

	return {
		agedWip: {
			title: 'Aged Wip Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: agedWipDateMonthEndInclusive,
			startDateLabel: 'Report Date',
			startDateReadOnly: false,
			showPartnerFilter: true,
			showManagerFilter: true,
			showWipFromDate: false,
			reportEndpoint: ({ startDate, partnerStaffXpmID, managerStaffXpmID, wipFromDate }) => {
				const clientWhereArr = []

				if (partnerStaffXpmID) {
					clientWhereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
				}
				if (managerStaffXpmID) {
					clientWhereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
				}

				const clientWhere = clientWhereArr.join('%2B')

				return `/agedwip?compdate=${startDate}${wipFromDate ? `&wipfromdate=${wipFromDate}` : ''}${clientWhere ? `&clientwhere=${clientWhere}` : ''}`
			},
		},
		receipts: {
			title: 'Receipts Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: DateTime.now().startOf('month').toISODate(),
			startDateLabel: 'From',
			showEndDate: true,
			showDepartmentFilter: true,
			showPartnerFilter: true,
			showManagerFilter: true,
			endDateLabel: 'To (Inclusive)',
			reportEndpoint: ({ startDate, endDate, xeroOrgID, partnerStaffXpmID, managerStaffXpmID }) => {
				const paymentWhereArr = [`xp.XeroPaymentDate>=${startDate}`]

				if (endDate) {
					paymentWhereArr.push(`xp.XeroPaymentDate<=${endDate}`)
				}

				const whereArr = [`paymentwhere=${paymentWhereArr.join('%2B')}`]

				if (xeroOrgID) {
					whereArr.push(`invoicewhere=i.XeroOrgID==${xeroOrgID}`)
				}

				const clientWhereArr = []

				if (partnerStaffXpmID) {
					clientWhereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
				}
				if (managerStaffXpmID) {
					clientWhereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
				}

				const clientWhere = clientWhereArr.join('%2B')

				if (clientWhere) {
					whereArr.push(`clientwhere=${clientWhere}`)
				}

				return `/receipts?${whereArr.join('&')}`
			},
		},
		wipAnalysis: {
			title: 'WIP Analysis',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: agedWipDate,
			showEmployeeFilter: true,
			employeeFilterLabel: 'Time from Employee(s)',
			showPartnerFilter: true,
			showManagerFilter: true,
			reportEndpoint: ({ startDate, employeeStaffXpmIDs, partnerStaffXpmID, managerStaffXpmID }) => {
				const whereArr = [`t.TimeDate>=${startDate}`]

				if (employeeStaffXpmIDs && employeeStaffXpmIDs.length > 0) {
					whereArr.push(`t.TimeStaffXpmID=in(${employeeStaffXpmIDs.join(',')})`)
				}

				if (partnerStaffXpmID) {
					whereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
				}
				if (managerStaffXpmID) {
					whereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
				}

				return `/wipanalysis?where=${whereArr.join('%2B')}`
			},
		},
		wipMovement: wipMovementSettings('WIP Movement', 'wipmovement'),
		wipMovementDetailed: wipMovementSettings('WIP Movement (Detailed Breakdown)', 'wipmovementdetailed'),
		jobProfitability: {
			title: 'Job Profitability',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: DateTime.now().minus({ months: 1 }).startOf('month').toISODate(),
			startDateLabel: 'From Invoice Date',
			showEndDate: true,
			endDateIsRequired: true,
			endDateDefault: DateTime.now().minus({ months: 1 }).endOf('month').toISODate(),
			endDateLabel: 'To Invoice Date (Inclusive)',
			showJobFilter: true,
			showClientGroupFilter: true,
			showClientFilter: true,
			reportEndpoint: ({ startDate, endDate, jobIDs }) =>
				`/jobprofitability?start=${startDate}&end=${endDate}${jobIDs && jobIDs.length > 0 ? `&where=j.JobID=in(${jobIDs.join(',')})` : ''}`,
		},
		clientProfitability: {
			title: 'Client Profitability',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: DateTime.now().minus({ months: 1 }).startOf('month').toISODate(),
			startDateLabel: 'From Invoice Date',
			showEndDate: true,
			endDateIsRequired: true,
			endDateDefault: DateTime.now().minus({ months: 1 }).endOf('month').toISODate(),
			endDateLabel: 'To Invoice Date (Inclusive)',
			showDepartmentFilter: true,
			showPartnerFilter: true,
			showManagerFilter: true,
			showClientGroupFilter: true,
			showClientFilter: true,
			reportEndpoint: ({ startDate, endDate, xeroOrgID, partnerStaffXpmID, managerStaffXpmID, clientGroupXpmID, clientXpmID }) => {
				const whereArr = [`start=${startDate}&end=${endDate}`]

				const clientWhereArr = []

				if (xeroOrgID) {
					clientWhereArr.push(`c.XeroOrgID==${xeroOrgID}`)
				}
				if (partnerStaffXpmID) {
					clientWhereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
				}
				if (managerStaffXpmID) {
					clientWhereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
				}
				if (clientGroupXpmID) {
					clientWhereArr.push(`c.ClientGroupXpmID==${clientGroupXpmID}`)
				}
				if (clientXpmID) {
					clientWhereArr.push(`c.ClientXpmID==${clientXpmID}`)
				}

				const clientWhere = clientWhereArr.join('%2B')

				if (clientWhere) {
					whereArr.push(`clientwhere=${clientWhere}`)
				}

				return `/clientprofitability?${whereArr.join('&')}`
			},
		},
		invoices: {
			title: 'Invoices Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: agedWipDate,
			showEndDate: true,
			endDateDefault: agedWipDateMonthEndInclusive,
			endDateLabel: 'To (Inclusive)',
			showDepartmentFilter: true,
			showPartnerFilter: true,
			showManagerFilter: true,
			showClientFilter: true,
			showClientGroupFilter: true,
			reportEndpoint: ({ startDate, endDate, xeroOrgID, partnerStaffXpmID, managerStaffXpmID, clientGroupXpmID, clientXpmID }) => {
				const invoiceWhereArr = [`i.InvoiceDate>=${startDate}`]

				if (endDate) {
					invoiceWhereArr.push(`i.InvoiceDate<=${endDate}`)
				}

				if (xeroOrgID) {
					invoiceWhereArr.push(`i.XeroOrgID==${xeroOrgID}`)
				}

				const whereArr = [`invoicewhere=${invoiceWhereArr.join('%2B')}`]

				const clientWhereArr = []

				if (partnerStaffXpmID) {
					clientWhereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
				}
				if (managerStaffXpmID) {
					clientWhereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
				}
				if (clientGroupXpmID) {
					clientWhereArr.push(`c.ClientGroupXpmID==${clientGroupXpmID}`)
				}
				if (clientXpmID) {
					clientWhereArr.push(`c.ClientXpmID==${clientXpmID}`)
				}

				const clientWhere = clientWhereArr.join('%2B')

				if (clientWhere) {
					whereArr.push(`clientwhere=${clientWhere}`)
				}

				return `/invoices?${whereArr.join('&')}`
			},
		},
		agedDebtors: {
			title: 'Aged Debtors Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: DateTime.now().toISODate(),
			startDateLabel: 'Reporting Date',
			showDepartmentFilter: true,
			showPartnerFilter: true,
			showManagerFilter: true,
			showClientGroupFilter: true,
			showClientFilter: true,
			reportEndpoint: ({ startDate, xeroOrgID, partnerStaffXpmID, managerStaffXpmID, clientGroupXpmID, clientXpmID }) => {
				const whereArr = [`compdate=${startDate}`]

				const clientWhereArr = []

				if (partnerStaffXpmID) {
					clientWhereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
				}
				if (managerStaffXpmID) {
					clientWhereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
				}
				if (clientXpmID) {
					clientWhereArr.push(`c.ClientXpmID==${clientXpmID}`)
				}

				const clientWhere = clientWhereArr.join('%2B')

				if (clientWhere) {
					whereArr.push(`clientwhere=${clientWhere}`)
				}

				if (xeroOrgID) {
					whereArr.push(`invoicewhere=i.XeroOrgID==${xeroOrgID}`)
				}

				if (clientGroupXpmID) {
					whereArr.push(`clientgroupwhere=cg.ClientGroupXpmID==${clientGroupXpmID}`)
				}

				return `/ageddebtors?${whereArr.join('&')}`
			},
		},
		debtorMovement: {
			title: 'Debtor Movement Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: agedWipDate,
			startDateLabel: 'From',
			showEndDate: true,
			endDateIsRequired: true,
			endDateDefault: agedWipDateMonthEndInclusive,
			endDateLabel: 'To (Inclusive)',
			showDepartmentFilter: true,
			showPartnerFilter: true,
			showManagerFilter: true,
			reportEndpoint: ({ startDate, endDate, xeroOrgID, partnerStaffXpmID, managerStaffXpmID }) => {
				const clientWhereArr = []

				if (partnerStaffXpmID) {
					clientWhereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
				}
				if (managerStaffXpmID) {
					clientWhereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
				}

				const clientWhere = clientWhereArr.join('%2B')

				return `/debtormovement?start=${startDate}&end=${endDate}${xeroOrgID ? `&invoicewhere=i.XeroOrgID==${xeroOrgID}` : ''}${
					clientWhere ? `&clientwhere=${clientWhere}` : ''
				}`
			},
		},
		completeDebtWipAnalysis: {
			title: 'Complete Debt and WIP Analysis Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: agedWipDateMonthEndInclusive,
			startDateLabel: 'Reporting Date',
			showManagerFilter: true,
			showPartnerFilter: true,
			showWipFromDate: false,
			reportEndpoint: ({ startDate, partnerStaffXpmID, managerStaffXpmID }) => {
				const clientWhereArr = []

				if (partnerStaffXpmID) {
					clientWhereArr.push(`c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}`)
				}
				if (managerStaffXpmID) {
					clientWhereArr.push(`c.ClientJobManagerStaffXpmID==${managerStaffXpmID}`)
				}

				const clientWhere = clientWhereArr.join('%2B')

				return `/compdebtwipanalysis?start=${startDate}${clientWhere ? `&where=${clientWhere}` : ''}`
			},
		},
		employeeUtilisation: {
			title: 'Employee Utilisation Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: DateTime.now().minus({ months: 1 }).startOf('month').toISODate(),
			startDateLabel: 'From',
			showEndDate: true,
			endDateIsRequired: true,
			endDateDefault: DateTime.now().minus({ months: 1 }).endOf('month').toISODate(),
			endDateLabel: 'To (Inclusive)',
			showEmployeeFilter: true,
			reportEndpoint: ({ startDate, employeeStaffXpmIDs, endDate }) =>
				`/employeeutil?start=${startDate}&end=${endDate}${
					employeeStaffXpmIDs && employeeStaffXpmIDs.length > 0 ? `&where=s.StaffXpmID=in(${employeeStaffXpmIDs.join(',')})` : ''
				}`,
		},
		employeePerformance: {
			title: 'Employee Performance Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: DateTime.now().minus({ months: 1 }).startOf('month').toISODate(),
			startDateLabel: 'From',
			showEndDate: true,
			endDateIsRequired: true,
			endDateDefault: DateTime.now().minus({ months: 1 }).endOf('month').toISODate(),
			endDateLabel: 'To (Inclusive)',
			showPartnerFilter: true,
			partnerFilterLabel: 'Client Partner',
			showEmployeeFilter: true,
			showEmployeeCategoryFilter: true,
			reportEndpoint: ({ startDate, employeeStaffXpmIDs, endDate, partnerStaffXpmID, gradeID }) =>
				`/employeeperf?start=${startDate}&end=${endDate}${
					partnerStaffXpmID ? `&clientwhere=c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}` : ''
				}${employeeStaffXpmIDs && employeeStaffXpmIDs.length > 0 ? `&where=s.StaffXpmID=in(${employeeStaffXpmIDs.join(',')})` : ''}${
					gradeID ? `&gradeid=${gradeID}` : ''
				}`,
		},
		employeeProfitability: {
			title: 'Employee Profitability Report',
			showStartDate: true,
			startDateIsRequired: true,
			startDateDefault: DateTime.now().minus({ months: 1 }).startOf('month').toISODate(),
			startDateLabel: 'From',
			showEndDate: true,
			endDateIsRequired: true,
			endDateDefault: DateTime.now().minus({ months: 1 }).endOf('month').toISODate(),
			endDateLabel: 'To (Inclusive)',
			showPartnerFilter: true,
			partnerFilterLabel: 'Client Partner',
			showEmployeeFilter: true,
			showEmployeeCategoryFilter: true,
			reportEndpoint: ({ startDate, employeeStaffXpmIDs, endDate, partnerStaffXpmID, gradeID }) =>
				`/employeeprofitability?start=${startDate}&end=${endDate}${
					partnerStaffXpmID ? `&clientwhere=c.ClientAccountManagerStaffXpmID==${partnerStaffXpmID}` : ''
				}${employeeStaffXpmIDs && employeeStaffXpmIDs.length > 0 ? `&where=s.StaffXpmID=in(${employeeStaffXpmIDs.join(',')})` : ''}${
					gradeID ? `&gradeid=${gradeID}` : ''
				}`,
		},
		employeeGradeHistory: {
			title: 'Employee Grade History Report',
			startDateDefault: DateTime.now().toISODate(),
			showPartnerFilter: true,
			showEmployeeFilter: true,
			showRadioFilter: true,
			radioDefault: 'Current',
			radioOptions: ['Current', 'Full History'],
			reportEndpoint: ({ employeeStaffXpmIDs, partnerStaffXpmID, radioOption: currentOrFull }) =>
				`/employeegradehist?history=${currentOrFull === 'Current' ? 'curr' : 'full'}${
					partnerStaffXpmID ? `&where=StaffReportGroupStaffXpmID==${partnerStaffXpmID}` : ''
				}${employeeStaffXpmIDs && employeeStaffXpmIDs.length > 0 ? `&where=StaffXpmID=in(${employeeStaffXpmIDs.join(',')})` : ''}`,
		},
	} satisfies Record<string, ReportSettings>
}

const Reports = () => {
	const { appState } = React.useContext(AppContext)
	const role = appState.permission.roleID
	const currentBillySettings = appState.accounts.find((acc) => acc.accountID === appState.authState.attributes?.['custom:currentAccountId'])?.billySettings
	const agedWipDate = currentBillySettings?.agedWipDate.slice(0, 10) || null

	const [modalReportSettings, setModalReportSettings] = React.useState<ReportSettings | null>(null)

	if (!agedWipDate) {
		return (
			<Container>
				<Row className="pt-3">
					<Col>
						<h2>Reports</h2>
					</Col>
				</Row>
				<Row className="pt-3">
					<Col>
						<DashboardCard background="cornflower">
							<Loading show />
						</DashboardCard>
					</Col>
				</Row>
			</Container>
		)
	}

	const reportSettings = getReportSettings(agedWipDate)

	const colWidth = {
		name: 4,
		desc: 8,
	}

	return (
		<Container>
			<Row className="pt-3">
				<Col>
					<h2>Reports</h2>
				</Col>
			</Row>
			<Row className="pt-3">
				<Col>
					<DashboardCard background="cornflower">
						<Row>
							<Col>
								<h3>WIP</h3>
							</Col>
						</Row>
						<Row>
							<Col sm={colWidth.name}>
								<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.agedWip)}>
									Aged WIP Report
								</span>
							</Col>
							<Col sm={colWidth.desc}>
								<span className="span-body-1">All current WIP and days since incurred</span>
							</Col>
						</Row>
						<Row className="pt-2">
							<Col sm={colWidth.name}>
								<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.wipAnalysis)}>
									WIP Analysis
								</span>
							</Col>
							<Col sm={colWidth.desc}>
								<span className="span-body-1 ">Analysis of WIP incurred, billed, written off and deferred</span>
							</Col>
						</Row>
						<Row className="pt-2">
							<Col sm={colWidth.name}>
								<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.wipMovement)}>
									WIP Movement
								</span>
							</Col>
							<Col sm={colWidth.desc}>
								<span className="span-body-1">WIP Movement</span>
							</Col>
						</Row>
						<Row className="pt-2">
							<Col sm={colWidth.name}>
								<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.wipMovementDetailed)}>
									WIP Movement (Detailed Breakdown)
								</span>
							</Col>
							<Col sm={colWidth.desc}>
								<span className="span-body-1">WIP Movement with Opening WIP, Write Off and Write Up broken down by times/costs/estimates</span>
							</Col>
						</Row>
					</DashboardCard>
				</Col>
			</Row>
			{currentBillySettings && currentBillySettings.costRateIsRequired ? (
				<Row className="pt-3">
					<Col>
						<DashboardCard background="cornflower">
							<Row>
								<Col>
									<h3>Profitability</h3>
								</Col>
							</Row>
							<Row>
								<Col sm={colWidth.name}>
									<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.jobProfitability)}>
										Job Profitability
									</span>
								</Col>
								<Col sm={colWidth.desc}>
									<span className="span-body-1">Profitability of Jobs</span>
								</Col>
							</Row>
							{isDirectorSuperManagerEA.includes(role) && (
								<Row className="pt-2">
									<Col sm={colWidth.name}>
										<span
											className="span-body-1 span-bold span-link"
											onClick={() => setModalReportSettings(reportSettings.clientProfitability)}
										>
											Client Profitability
										</span>
									</Col>
									<Col sm={colWidth.desc}>
										<span className="span-body-1 ">Profitability of Client Groups and Customers</span>
									</Col>
								</Row>
							)}
						</DashboardCard>
					</Col>
				</Row>
			) : null}
			<Row className="pt-3">
				<Col>
					<DashboardCard background="cornflower">
						<Row>
							<Col>
								<h3>Invoice and Debt</h3>
							</Col>
						</Row>
						{isDirectorSuperManagerEA.includes(role) && (
							<Row>
								<Col sm={colWidth.name}>
									<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.invoices)}>
										Invoices Report
									</span>
								</Col>
								<Col sm={colWidth.desc}>
									<span className="span-body-1">All invoices raised within a period</span>
								</Col>
							</Row>
						)}
						{isDirectorSuperManagerEA.includes(role) && (
							<Row className="pt-2">
								<Col sm={colWidth.name}>
									<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.receipts)}>
										Receipts Report
									</span>
								</Col>
								<Col sm={colWidth.desc}>
									<span className="span-body-1">All receipts received within a period against the original invoice</span>
								</Col>
							</Row>
						)}
						<Row className="pt-2">
							<Col sm={colWidth.name}>
								<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.agedDebtors)}>
									Aged Debtors
								</span>
							</Col>
							<Col sm={colWidth.desc}>
								<span className="span-body-1">All outstanding invoices and days outstanding</span>
							</Col>
						</Row>
						{isDirectorSuperManagerEA.includes(role) && (
							<Row className="pt-2">
								<Col sm={colWidth.name}>
									<span className="span-body-1 span-bold span-link" onClick={() => setModalReportSettings(reportSettings.debtorMovement)}>
										Debtor Movement
									</span>
								</Col>
								<Col sm={colWidth.desc}>
									<span className="span-body-1">-</span>
								</Col>
							</Row>
						)}
						{isDirector.includes(role) && (
							<Row className="pt-2">
								<Col sm={colWidth.name}>
									<span
										className="span-body-1 span-bold span-link"
										onClick={() => setModalReportSettings(reportSettings.completeDebtWipAnalysis)}
									>
										Complete Debt and WIP Analysis
									</span>
								</Col>
								<Col sm={colWidth.desc}>
									<span className="span-body-1">Complete analysis of all debt and WIP for the firm</span>
								</Col>
							</Row>
						)}
					</DashboardCard>
				</Col>
			</Row>

			{isDirectorSuperManager.includes(role) && (
				<Row className="pt-3">
					<Col>
						<DashboardCard background="cornflower">
							<Row>
								<Col>
									<h3>Employee</h3>
								</Col>
							</Row>
							<Row>
								<Col sm={colWidth.name}>
									<span
										className="span-body-1 span-bold span-link"
										onClick={() => setModalReportSettings(reportSettings.employeeUtilisation)}
									>
										Employee Utilisation
									</span>
								</Col>
								<Col sm={colWidth.desc}>
									<span className="span-body-1">-</span>
								</Col>
							</Row>
							<Row className="pt-2">
								<Col sm={colWidth.name}>
									<span
										className="span-body-1 span-bold span-link"
										onClick={() => setModalReportSettings(reportSettings.employeePerformance)}
									>
										Employee Performance
									</span>
								</Col>
								<Col sm={colWidth.desc}>
									<span className="span-body-1">-</span>
								</Col>
							</Row>
							{currentBillySettings && currentBillySettings.costRateIsRequired && isDirector.includes(role) && (
								<Row className="pt-2">
									<Col sm={colWidth.name}>
										<span
											className="span-body-1 span-bold span-link"
											onClick={() => setModalReportSettings(reportSettings.employeeProfitability)}
										>
											Employee Profitability
										</span>
									</Col>
									<Col sm={colWidth.desc}>
										<span className="span-body-1">Profitability of each employee</span>
									</Col>
								</Row>
							)}
							<Row className="pt-2">
								<Col sm={colWidth.name}>
									<span
										className="span-body-1 span-bold span-link"
										onClick={() => setModalReportSettings(reportSettings.employeeGradeHistory)}
									>
										Employee Grade History
									</span>
								</Col>
								<Col sm={colWidth.desc}>
									<span className="span-body-1">-</span>
								</Col>
							</Row>
						</DashboardCard>
					</Col>
				</Row>
			)}

			{modalReportSettings && <ReportModal onHide={() => setModalReportSettings(null)} reportSettings={modalReportSettings} />}
		</Container>
	)
}

export default Reports
